import { useState } from 'react'
import type { ColKind, RowKind, View } from './derive'
import { CheckAccess } from './CheckAccess'
import { DirectList } from './DirectList'
import { GettingStarted } from './GettingStarted'
import { useRemembered } from './parts'
import { GroupPanel } from './GroupPanel'
import { OtherRules } from './OtherRules'
import { Recent } from './Recent'
import { RulesMatrix } from './RulesMatrix'
import { TeamPanel } from './TeamPanel'

/**
 * Access: teams down the side, groups of machines across the top, a square
 * where they meet. ~5 × 6 squares whether there are 6 robots or 206 — that is
 * the whole reason access is given this way. Beside it, the one question
 * people actually ask ("can Maya get into R-147?"), the exceptions, and what
 * changed recently.
 */
export function AccessTab({ view }: { view: View }): JSX.Element {
  const [open, setOpen] = useState<{ kind: 'team' | 'group'; id: string | null } | null>(null)
  // The grid opens the way it was left; the first time, People × Machines.
  const [rows, setRows] = useRemembered<RowKind>('kronos.connectivity.rows', 'people', ['people', 'teams'])
  const [cols, setCols] = useRemembered<ColKind>('kronos.connectivity.cols', 'machines', ['machines', 'groups'])
  return (
    <div className="cx-columns">
      <div className="cx-main">
        <GettingStarted view={view} />
        <RulesMatrix
          view={view}
          rows={rows}
          cols={cols}
          onRows={setRows}
          onCols={setCols}
          onTeam={(id) => setOpen({ kind: 'team', id })}
          onGroup={(id) => setOpen({ kind: 'group', id })}
        />
        <OtherRules base={view.page.base} />
      </div>
      <aside className="cx-side">
        <CheckAccess view={view} />
        <DirectList view={view} />
        <Recent events={view.page.events} />
      </aside>
      {open?.kind === 'team' ? <TeamPanel view={view} teamId={open.id} onClose={() => setOpen(null)} /> : null}
      {open?.kind === 'group' ? <GroupPanel view={view} groupId={open.id} onClose={() => setOpen(null)} /> : null}
    </div>
  )
}
